<template>
  <LLConfirmModalTransition v-if="showContent" appear>
    <LLConfirmModal
      v-if="isAwaitingPermission"
      key="awaiting"
      class="local-notification-prompt"
      :max-width="530"
    >
      <template #title>
        {{ $t('popups.local-notification-prompt.permission_title') }}
      </template>
      <template #default>
        <p>
          {{
            $t('popups.local-notification-prompt.permission_description') ||
            'Please allow notifications when your browser asks for permission.'
          }}
        </p>
      </template>
    </LLConfirmModal>

    <LLConfirmModal
      v-else-if="showIOSInstructions"
      key="ios"
      class="local-notification-prompt"
      :max-width="530"
    >
      <template #title>
        {{ $t('popups.local-notification-prompt.ios_title') }}
      </template>
      <template #default>
        <i18n
          path="popups.local-notification-prompt.ios_body"
          tag="p"
          class="local-notification-prompt__ios-text"
        >
          <template #icon>
            <span class="local-notification-prompt__ios-inline-icon"><IOSShareIcon /></span>
          </template>
          <template #addToHomeScreen>
            <strong>{{ $t('popups.local-notification-prompt.ios_add_to_home_screen') }}</strong>
          </template>
        </i18n>
      </template>
      <template #actions>
        <LLButton primary small @click="onClose">
          {{ $t('popups.local-notification-prompt.got_it_button') || 'Got it' }}
        </LLButton>
      </template>
    </LLConfirmModal>

    <LLConfirmModal
      v-else-if="permissionDenied"
      key="denied"
      class="local-notification-prompt"
      :max-width="530"
    >
      <template #title>
        {{
          unsupported
            ? $t('popups.local-notification-prompt.unsupported_title')
            : $t('popups.local-notification-prompt.denied_title') || 'Notifications blocked'
        }}
      </template>
      <template #default>
        <p>
          {{
            unsupported
              ? $t('popups.local-notification-prompt.unsupported')
              : $t('popups.local-notification-prompt.denied') ||
                'This is usually caused by either your browser\u2019s notification settings or your device\u2019s system settings for this browser. Check both, then try again. If you need further assistance, please feel free to contact us at help@looklook.app.'
          }}
        </p>
      </template>
      <template #actions>
        <LLButton v-if="!unsupported" primary small @click="onEnable">
          {{ $t('popups.local-notification-prompt.try_again_button') || 'Try Again' }}
        </LLButton>
        <LLButton secondary small @click="onClose">
          {{ $t('popups.local-notification-prompt.close_button') || 'Close' }}
        </LLButton>
      </template>
    </LLConfirmModal>

    <LLConfirmModal v-else key="enable" class="local-notification-prompt" :max-width="530">
      <template #title>
        {{ $t('popups.local-notification-prompt.title') }}
      </template>
      <template #default>
        <p>{{ $t('popups.local-notification-prompt.description') }}</p>
      </template>
      <template #actions>
        <div class="local-notification-prompt__actions">
          <div class="local-notification-prompt__buttons">
            <LLButton primary small @click="onEnable">
              {{ $t('popups.local-notification-prompt.enable_button') || 'Enable Notifications' }}
            </LLButton>
            <LLButton secondary small @click="onClose">
              {{ $t('popups.local-notification-prompt.skip_button') || 'Not now' }}
            </LLButton>
          </div>
          <a
            class="local-notification-prompt__help-link"
            :href="helpNotificationsUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('popups.local-notification-prompt.help_link') || 'Need help? View the setup guide' }}
          </a>
        </div>
      </template>
    </LLConfirmModal>
  </LLConfirmModalTransition>
</template>

<script>
import LLConfirmModal from '@/components/common/LLConfirmModal.vue'
import LLConfirmModalTransition from '@/components/common/LLConfirmModal/LLConfirmModalTransition.vue'
import LLButton from '@/components/common/ui-components/LLButton.vue'
import IOSShareIcon from '@/assets/icons/IOSShareIcon.vue'
import { getPermissionStatus } from '@/utils/pushNotifications'
import { enableNotifications } from '@/utils/notificationGate'
import { HELP_NOTIFICATIONS_URL } from '@/common/helpLinks'
import {
  markIOSInstallPromptDismissed,
  markNotificationPromptShown,
  shouldShowNotificationPrompt
} from '@/utils/notificationPrompt'

export default {
  name: 'LocalNotificationPrompt',
  components: { IOSShareIcon, LLButton, LLConfirmModalTransition, LLConfirmModal },
  props: {
    forceShow: { type: Boolean, default: false },
    startAwaiting: { type: Boolean, default: false },
    unsupported: { type: Boolean, default: false },
    forceIOS: { type: Boolean, default: false }
  },
  data() {
    return {
      showContent: false,
      permissionDenied: false,
      isAwaitingPermission: false,
      isProcessing: false,
      showIOSInstructions: false
    }
  },
  computed: {
    helpNotificationsUrl() {
      return HELP_NOTIFICATIONS_URL
    }
  },
  async created() {
    if (this.unsupported) {
      this.showContent = true
      this.permissionDenied = true
      return
    }

    if (this.forceIOS) {
      this.showContent = true
      this.showIOSInstructions = true
      return
    }

    if (this.startAwaiting) {
      // Profile checkbox flow: the shared gate already triggered the browser
      // permission prompt synchronously (user gesture preserved), so show the
      // awaiting helper ("Please allow notifications…") instead of the Enable
      // button screen — the request is in flight, no button is needed.
      this.showContent = true
      this.isAwaitingPermission = true
      return
    }

    const shouldShow = await shouldShowNotificationPrompt(this.$api, this.$store, {
      force: this.forceShow
    })

    if (!shouldShow) {
      this.closeWithoutMarking()
      return
    }

    if (getPermissionStatus() === 'denied') {
      this.showContent = true
      this.permissionDenied = true
      return
    }

    this.showContent = true
  },
  methods: {
    markShown() {
      markNotificationPromptShown(this.$store)
    },
    closeWithoutMarking() {
      this.showContent = false
      this.$emit('close')
    },
    async onEnable() {
      if (this.isProcessing) return
      this.isProcessing = true

      try {
        // Single shared gate (P2): same decision flow as the profile checkbox.
        await enableNotifications(this.$api, this.$store, {
          onIOSCoach: () => {
            this.isAwaitingPermission = false
            this.showIOSInstructions = true
          },
          onUnsupported: () => {
            this.isAwaitingPermission = false
            this.permissionDenied = true
          },
          onAwaitPermission: () => {
            this.permissionDenied = false
            this.showIOSInstructions = false
            this.isAwaitingPermission = true
          },
          onSubscribed: () => {
            this.markShown()
            this.$notify({
              group: 'main',
              type: 'success',
              title: this.$t('popups.local-notification-prompt.opted_in_toast')
            })
            this.$emit('close')
          },
          onDenied: () => {
            this.isAwaitingPermission = false
            this.permissionDenied = true
          },
          onFailed: () => {
            // Browser prompt dismissed without a decision — just close
            this.closeWithoutMarking()
          }
        })
      } catch (err) {
        this.isAwaitingPermission = false
        this.permissionDenied = true
      } finally {
        this.isProcessing = false
      }
    },
    onClose() {
      if (this.showIOSInstructions) {
        markIOSInstallPromptDismissed(this.$store)
        this.$emit('close')
        return
      }

      this.markShown()
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
.local-notification-prompt {
  ::v-deep .LLConfirmModal__title {
    overflow: visible;
    text-overflow: initial;
    white-space: normal;
  }

  &__ios-text {
    line-height: 1.6;
  }
  &__ios-inline-icon {
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    margin: 0 2px;
    color: var(--color-primary-01);
    background: var(--color-neutral-01-25);
    border-radius: 4px;
  }
  &__ios-inline-icon ::v-deep .icon {
    width: 14px;
    height: 14px;
  }
  &__actions {
    @apply w-full flex flex-col items-end;
  }
  &__buttons {
    @apply flex gap-2;
  }
  &__help-link {
    @apply mt-3 text-body-02 text-neutral-01-500 underline;
  }
}
</style>
